import * as React from "react"
import { Col, Spinner, Button, Image, Form, InputGroup } from "react-bootstrap"
import { concat, of, Subject, Subscription } from "rxjs"
import { filter, map, switchMap } from "rxjs/operators"
import { toast } from "react-toastify"
import { Typeahead } from "react-bootstrap-typeahead"
import { FaAngleDown, FaPencilAlt, FaTimes } from "react-icons/fa"
import PoolData from "../../../api/Randoms/RandomsModel/PoolData"
import { PoolUpdateObservable } from "../../../api/Randoms/PoolSave"
import CloseProgramIntervalObservable from "../../../api/Randoms/CloseProgramInterval"
import LabServiceRegulatoryModesGetObservable from "../../../api/LabService/LabServiceRegulatoryModesGet"
import RegulatoryMode from "../../../api/LabService/LabServiceModel/RegulatoryMode"
import SelectionIntervalsGetObservable from "../../../api/Randoms/SelectionIntervalsGet"
import LabAccount from "../../../api/Randoms/RandomsModel/LabAccount"
import LabAccountListGetObservable from "../../../api/Randoms/LabAccountsGet"
import LabPanel from "../../../api/Randoms/RandomsModel/LabPanel"
import LabPanelsGetObservable from "../../../api/Randoms/LabPanelsGet"
import ActionButton from "../../ActionButton"
import ConfirmationModal from "../../ConfirmationModal"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns"
import PrimaryIDType from "../../../api/PrimaryIDTypes/PrimaryIDType"
import { PoolPrimaryIDTypesGetObservable } from "../../../api/PrimaryIDTypes/PrimaryIDTypesGet"
import { IsOnTrack } from "../../../App";
import MissedPeriodsModal from "./MissedPeriodsModal"
import Checkbox from "../../Checkbox"
import LoadingIndicatorWrapper from "../../LoadingIndicatorWrapper"

export enum PoolVariant {
  CloseProgramYear
}

interface PoolGeneralInfoProps {
  poolId: number
  variant?: PoolVariant
  edit?: boolean
  poolData?: PoolData
  onPoolUpdate: () => void
  isConsortium?: boolean
  isPortalAdmin?: boolean
}

interface PoolGeneralInfoEditValidity {
  isPoolNameValid: boolean
  isFederallyRegulatedValid: boolean
  isRegulatoryModeValid: boolean
  isSelectionIntervalValid: boolean
  isCalcTypeValid: boolean
  isDrugSampleSizeValid: boolean
  isAlcoholSampleSizeValid: boolean
  isLabAccountValid: boolean
  isLabPanelValid: boolean
  isPoolMembershipTypeValid: boolean
  isPrimaryIDTypeValid: boolean
  isPanelCodeValid: boolean
}

interface PoolGeneralInfoState extends PoolGeneralInfoEditValidity {
  data?: PoolData
  editData?: PoolData
  selectedCalcType?: CalcType
  selectedLabAccount?: LabAccount
  selectedLabPanel?: LabPanel
  editing: boolean
  saving: boolean
  showSaveConfirmModal: boolean
  regulatoryModesLoading: boolean
  selectionIntervalsLoading: boolean
  poolLoading: boolean
  labAccountsLoading: boolean
  labPanelsLoading: boolean
  regulatoryModes?: RegulatoryMode[]
  selectionIntervals?: string[]
  labAccounts?: LabAccount[]
  labPanels?: LabPanel[]
  availableLabPanels?: LabPanel[]
  startInEditMode: boolean
  primaryIDTypesLoading: boolean
  primaryIDTypes?: PrimaryIDType[]
  showMissingModal: boolean
  panelCode: string
  selectedRegulatoryModes: RegulatoryMode[]
  availableToSelectRegModes: RegulatoryMode[]
}

const calcTypePercentage = "Program Percentage"
const calcTypeFixed = "Program Fixed"

const poolMembershipTypeFileImport = "File Import"
const poolMembershipTypeCriteria = "Criteria"

const yes = "Yes"
const no = "No"

interface CalcType { Name: string, Code: string, ShortCode: string }
const calcTypes: CalcType[] = [
  { Name: "Annual Percentage", Code: calcTypePercentage, ShortCode: "Rate" },
  { Name: "Fixed Number of Selections for Program Year", Code: calcTypeFixed, ShortCode: "Fixed" }
]

export default class PoolGeneralInfo extends React.PureComponent<PoolGeneralInfoProps, PoolGeneralInfoState> {
  positiveValidity: PoolGeneralInfoEditValidity = {
    isPoolNameValid: true,
    isFederallyRegulatedValid: true,
    isRegulatoryModeValid: true,
    isSelectionIntervalValid: true,
    isCalcTypeValid: true,
    isDrugSampleSizeValid: true,
    isAlcoholSampleSizeValid: true,
    isLabAccountValid: true,
    isLabPanelValid: true,
    isPoolMembershipTypeValid: true,
    isPrimaryIDTypeValid: true,
    isPanelCodeValid: true
  }
  state: PoolGeneralInfoState = {
    ...this.positiveValidity,
    editing: !!this.props.edit,
    saving: false,
    showSaveConfirmModal: false,
    regulatoryModesLoading: false,
    selectionIntervalsLoading: false,
    poolLoading: false,
    labAccountsLoading: false,
    labPanelsLoading: false,
    startInEditMode: this.props.variant === PoolVariant.CloseProgramYear,
    primaryIDTypesLoading: false,
    showMissingModal: false,
    panelCode: "",
    selectedRegulatoryModes: [],
    availableToSelectRegModes: []

  }

  regulatoryModeTypeahead = React.createRef<Typeahead<RegulatoryMode>>()
  federallyRegulatedTypeahead = React.createRef<Typeahead<string>>()
  selectionIntervalTypeahead = React.createRef<Typeahead<string>>()
  calcTypeTypeahead = React.createRef<Typeahead<CalcType>>()
  labAccountTypeahead = React.createRef<Typeahead<LabAccount>>()
  panelTypeahead = React.createRef<Typeahead<LabPanel>>()
  poolMembershipTypeTypeahead = React.createRef<Typeahead<string>>()

  labAccount$ = new Subject<LabAccount>()
  labAccountSubscription?: Subscription
  regulatoryModesSubscription?: Subscription
  selectionIntervalsSubscription?: Subscription
  labAccountsSubscription?: Subscription
  updateSubscription?: Subscription
  closeProgramIntervalSubscription?: Subscription
  primaryIDTypesSubscription?: Subscription
  subscriptions = new Subscription()

  componentDidMount() {
    this.loadPoolData(true)
    if (this.props.poolData) {
      if (this.props.poolData.DOT || this.props.poolData.RegulatoryModes.length > 0) {
        this.loadRegulatoryModes()
      }
    }
  }

  componentWillUnmount() {
    this.labAccountSubscription && this.labAccountSubscription.unsubscribe()
    this.regulatoryModesSubscription && this.regulatoryModesSubscription.unsubscribe()
    this.selectionIntervalsSubscription && this.selectionIntervalsSubscription.unsubscribe()
    this.labAccountsSubscription && this.labAccountsSubscription.unsubscribe()
    this.updateSubscription && this.updateSubscription.unsubscribe()
    this.closeProgramIntervalSubscription && this.closeProgramIntervalSubscription.unsubscribe()
    this.subscriptions.unsubscribe()
  }

  loadPoolData = (initial: boolean = false) => {
    if (!initial) this.props.onPoolUpdate()
    const { startInEditMode } = this.state
    this.setState({ data: this.props.poolData, editData: undefined, editing: false, startInEditMode: false })
    startInEditMode && this.handleToggleEditMode()
  }

  loadRegulatoryModes = () => {
    if (!this.state.regulatoryModes) {
      let dt = this.state.data
      if (!dt) {
        dt = this.props.poolData
      }
      this.setState({ regulatoryModesLoading: true })
      this.regulatoryModesSubscription && this.regulatoryModesSubscription.unsubscribe()
      this.regulatoryModesSubscription = LabServiceRegulatoryModesGetObservable(true).subscribe(
        (data: RegulatoryMode[]) => {
            const selectedRegulatoryModes = data.filter(rm => rm.RegulatoryModeID === dt?.RegulatoryModeID || dt?.RegulatoryModes.includes(rm.RegulatoryModeID))
            const availableToSelectRegModes = data.filter(rm => !selectedRegulatoryModes.includes(rm))
            this.setState({
              regulatoryModes: data,
              regulatoryModesLoading: false,
              availableToSelectRegModes: availableToSelectRegModes,
              selectedRegulatoryModes: selectedRegulatoryModes
            })
        },
        () => this.setState({ regulatoryModesLoading: false })
      )
    }
  }

  loadSelectionIntervals = () => {
    if (!this.state.selectionIntervals) {
      this.setState({ selectionIntervalsLoading: true })
      this.selectionIntervalsSubscription && this.selectionIntervalsSubscription.unsubscribe()
      this.selectionIntervalsSubscription = SelectionIntervalsGetObservable().subscribe(
        (data: string[]) => this.setState({ selectionIntervals: data, selectionIntervalsLoading: false }),
        () => this.setState({ selectionIntervalsLoading: false })
      )
    }
  }

  createPanelsSubscription = () => {
    const link = sessionStorage.getItem("company-selection-link") || ""
    if (!this.labAccountSubscription) {
      this.labAccountSubscription = this.labAccount$.pipe(
        filter(labAccount => labAccount !== undefined),
        switchMap((labAccount: LabAccount) => concat(
          of({ panelsLoading: true }),
          LabPanelsGetObservable(link, labAccount.LabAccount, labAccount.LabClientMemberId).pipe(
            map((data) => ({ panelsLoading: false, items: data }))
          )))
      ).subscribe(
        (data: { panelsLoading: boolean, items?: LabPanel[] }) => this.setState(prevState => {
          return {
            labPanelsLoading: data.panelsLoading,
            labPanels: data.items,
            availableLabPanels: data.items ? data.items.filter(((labPanel: LabPanel) => labPanel.DOT === prevState.editData?.DOT)) : undefined,
            selectedLabPanel: data.items ? data.items.find(item => item.PanelID === prevState.editData?.PanelID) : undefined
          }
        }),
        () => this.setState({ labPanelsLoading: false })
      )
    }
  }

  loadLabAccounts = () => {
    const link = sessionStorage.getItem("company-selection-link") || ""
    if (!this.state.labAccounts) {
      this.setState({ labAccountsLoading: true })
      this.labAccountsSubscription && this.labAccountsSubscription.unsubscribe()
      this.labAccountsSubscription = LabAccountListGetObservable(link).subscribe(
        (data: LabAccount[]) => {
          const selectedLabAccount = data.find(item => item.LabAccount === this.state.editData?.LabAccountNumber)
          this.setState({
            labAccounts: data,
            labAccountsLoading: false,
            selectedLabAccount: selectedLabAccount
          })
          this.labAccount$.next(selectedLabAccount)
        },
        () => this.setState({ labAccountsLoading: false })
      )
    }
  }
  loadPrimaryIDTypes = () => {
    if (!this.state.primaryIDTypes) {
      this.setState({ primaryIDTypesLoading: true })
      this.primaryIDTypesSubscription && this.primaryIDTypesSubscription.unsubscribe()
      this.primaryIDTypesSubscription = PoolPrimaryIDTypesGetObservable().subscribe(
        (data: PrimaryIDType[]) => this.setState({ primaryIDTypes: data, primaryIDTypesLoading: false }),
        () => this.setState({ primaryIDTypesLoading: false })
      )
    }
  }

  handleToggleEditMode = () => {
    if (this.state.editing) {
      const selectedRegulatoryModes = this.state.regulatoryModes?.filter(rm => rm.RegulatoryModeID === this.state.data?.RegulatoryModeID || this.state.data?.RegulatoryModes.includes(rm.RegulatoryModeID))
      const availableToSelectRegModes = this.state.regulatoryModes?.filter(rm => !selectedRegulatoryModes?.includes(rm))
      this.setState({
        ...this.positiveValidity,
        editing: false,
        editData: undefined,
        selectedCalcType: undefined,
        selectedLabAccount: undefined,
        selectedLabPanel: undefined,
        selectedRegulatoryModes: selectedRegulatoryModes || [],
        availableToSelectRegModes: availableToSelectRegModes || [],
      })
    } else {
      const selectedLabAccount = this.state.labAccounts ? this.state.labAccounts.find(item => item.LabAccount === this.state.data?.LabAccountNumber) : undefined
      this.setState((prevState) => {
        return {
          editing: true,
          editData: prevState.data,
          selectedCalcType: calcTypes.find(item => item.Code === prevState.data?.FixedOrRate),
          selectedLabAccount: selectedLabAccount
        }
      })
      this.loadRegulatoryModes()
      this.loadSelectionIntervals()
      this.loadPrimaryIDTypes()
      this.createPanelsSubscription()
      this.loadLabAccounts()
      this.labAccount$.next(selectedLabAccount)
    }
  }

  handlePoolNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    this.state.editData && this.setState(prevState => {
      const editState = prevState.editData ? { ...prevState.editData, Name: value } : prevState.editData
      return { editData: editState, isPoolNameValid: value.length >= 8 }
    })
  }

  handlePanelCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    this.state.editData && this.setState(prevState => {
      const editState = prevState.editData ? { ...prevState.editData, PanelCode: value } : prevState.editData
      return { editData: editState, isPanelCodeValid: value.length >= 2 }
    })
  }

  handleFederallyRegulatedChange = (items: string[]) => {
    let value: boolean | undefined
    switch (items[0]) {
      case yes: value = true; break
      case no: value = false; break
    }

    const availableLabPanels = this.state.labPanels ? this.state.labPanels.filter(((labPanel: LabPanel) => labPanel.DOT === value)) : undefined
    this.state.editData && this.setState(
      (prevState) => {
        const editData = prevState.editData ? { ...prevState.editData, DOT: value, RegulatoryMode: "" } : prevState.editData
        return {
          editData: editData,
          isFederallyRegulatedValid: value !== undefined,
          isRegulatoryModeValid: true,
          availableLabPanels: availableLabPanels,
          selectedLabPanel: undefined
        }
      })

    this.handleLabPanelChange([])
  }

  handleRegulatoryModeChange = (items: RegulatoryMode[]) => {
    const availableToSelectRegModes: RegulatoryMode[] = []
    const selectedRegModes: RegulatoryMode[] = items || []
    const availableRegModes: RegulatoryMode[] = this.state.regulatoryModes || []
    let regulatoryModeId = 0
    if (selectedRegModes.length > 0) {
      regulatoryModeId = selectedRegModes[0].RegulatoryModeID
    }
    for (let i = 0; i < availableRegModes.length; i++) {
      let is_selected = false;
      const availRegMode = availableRegModes[i]
      for (let j = 0; j < selectedRegModes.length; j++) {
        const selectedRegMode = selectedRegModes[j];
        if (availRegMode.RegulatoryModeID === selectedRegMode.RegulatoryModeID) {
          is_selected = true
          break
        }
      }
      if (is_selected) {
        continue
      }
      availableToSelectRegModes.push(availRegMode)
    }

    this.state.editData && this.setState(prevState => {
      const editData = prevState.editData ? {
        ...prevState.editData,
        RegulatoryMode: items[0]?.Name || "",
        RegulatoryModeID: items[0]?.RegulatoryModeID,
        RegulatoryModes: selectedRegModes.map(({ RegulatoryModeID }) => RegulatoryModeID),
      } : prevState.editData

      return {
        editData: editData,
        isRegulatoryModeValid: items[0] !== undefined,
        selectedRegulatoryModes: selectedRegModes,
        availableToSelectRegModes: availableToSelectRegModes,
      }
    })
  }

  handleSelectionIntervalChange = (items: string[]) => {
    this.state.editData && this.setState(prevState => {
      const editData = prevState.editData ? { ...prevState.editData, SelectionInterval: items[0] || "" } : prevState.editData
      return { editData: editData, isSelectionIntervalValid: items[0] !== undefined }
    })
  }

  handleCalcTypeChange = (items: CalcType[]) => {
    const calcTypeCode = items[0]?.Code || ""
    this.state.editData && this.setState(prevState => {
      const editData = prevState.editData ? {
        ...prevState.editData,
        FixedOrRate: calcTypeCode,
        DrugSampleSize: calcTypeCode === calcTypePercentage ? Math.min(100, prevState.editData?.DrugSampleSize || 0) : prevState.editData?.DrugSampleSize,
        AlcoholSampleSize: calcTypeCode === calcTypePercentage ? Math.min(100, prevState.editData?.AlcoholSampleSize || 0) : prevState.editData?.AlcoholSampleSize
      } : prevState.editData
      return {
        editData: editData,
        selectedCalcType: items[0],
        isCalcTypeValid: items[0] !== undefined
      }
    })
  }

  handleDateChange = (key: "ProgramIntervalStartDate" | "ProgramIntervalEndDate", date: Date | null) => {
    this.state.editData && this.setState(prevState => {
      const editData = prevState.editData ? { ...prevState.editData, [key]: date ? date.toISOString() : "" } : prevState.editData
      return {
        editData: editData
      }
    })
  }

  parsePoolSize = (value: string): number | undefined => {
    if (this.state.editData?.FixedOrRate === calcTypePercentage && /^(\s*|100|0|[1-9]{1}\d?)$/.test(value)) {
      return Number(value)
    }
    if (this.state.editData?.FixedOrRate === calcTypeFixed && /^(\s*|0|[1-9]{1}\d{0,18})$/.test(value)) {
      return Number(value)
    }
    return undefined
  }

  handleSampleSizeChangeChange = (what: keyof PoolData, validityField: keyof PoolGeneralInfoEditValidity, value: string) => {
    const numberValue = this.parsePoolSize(value)
    if (numberValue !== undefined) {
      this.state.editData && this.setState(prevState => {
        const editData = prevState.editData ? { ...prevState.editData, [what]: numberValue } : prevState.editData
        return { ...{}, editData: editData, [validityField]: numberValue >= 0 }
      })
    }
  }

  handleLabAccountChange = (items: LabAccount[]) => {
    this.state.editData && this.setState(prevState => {
      const editData = prevState.editData ? {
        ...prevState.editData,
        LabAccountNumber: items[0]?.LabAccount || "",
        Panel: "",
        PanelID: 0
      } : prevState.editData
      return {
        editData: editData,
        selectedLabAccount: items[0],
        selectedLabPanel: undefined,
        labPanels: undefined,
        isLabAccountValid: this.props.isConsortium || items[0] !== undefined
      }
    })
    this.labAccount$.next(items[0])
  }

  handleLabPanelChange = (items: LabPanel[]) => {
    this.state.editData && this.setState(prevState => {
      const editData = prevState.editData ? { ...prevState.editData, Panel: items[0]?.Description || "", PanelID: items[0]?.PanelID || 0 } : prevState.editData
      return {
        editData: editData,
        selectedLabPanel: items[0],
        isLabPanelValid: items[0] !== undefined
      }
    })
  }

  handlePoolMembershipTypeChange = (items: string[]) => {
    let value: boolean | undefined
    switch (items[0]) {
      case poolMembershipTypeFileImport: value = true; break
      case poolMembershipTypeCriteria: value = false; break
    }
    this.state.editData && this.setState(prevState => {
      const editData = prevState.editData ? { ...prevState.editData, UseFileImportForPoolMembership: value } : prevState.editData
      return {
        editData: editData,
        isPoolMembershipTypeValid: value !== undefined
      }
    })
  }

  handleNoSelectionChange = () => {
    this.state.editData && this.setState(prevState => {
      const editData = prevState.editData ? { ...prevState.editData, IncludeCompaniesWithNoSelections: !prevState.editData?.IncludeCompaniesWithNoSelections } : prevState.editData
      return {
        editData: editData
      }
    })
  }

  handlePrimaryIDTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value || ""
    const items: PrimaryIDType[] = this.state.primaryIDTypes || []
    const ind = items.findIndex(item => item.PrimaryIDType === value);
    this.state.editData && this.setState(prevState => {
      const editState = prevState.editData ? { ...prevState.editData, PrimaryIDType: value } : prevState.editData
      return { editData: editState, isPrimaryIDTypeValid: ind >= 0 }
    })
  }
  handleRunOnlyByCompanyAdminChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.state.editData?.RunOnlyByPortalAdmin) {
      return
    }
    const checked = event.target.checked
    if (!checked) {
      return
    }
    this.state.editData && this.setState(prevState => {
      const editData = prevState.editData ? { ...prevState.editData, RunOnlyByPortalAdmin: !checked } : prevState.editData
      return {
        editData: editData
      }
    })
  }

  handleRunOnlyByPortalAdminChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (this.state.editData?.RunOnlyByPortalAdmin) {
      return
    }
    const checked = event.target.checked
    if (!checked) {
      return
    }
    this.state.editData && this.setState(prevState => {
      const editData = prevState.editData ? { ...prevState.editData, RunOnlyByPortalAdmin: checked } : prevState.editData
      return {
        editData: editData
      }
    })
  }

  handleSubmit = () => {
    if (!this.state.editData) {
      return
    }
    const editData = this.state.editData!
    const isConsortium = !!this.props.isConsortium

    const sampleSizeValid = (size: number, calcType: string): boolean => {
      if (size < 0) {
        return false
      }
      if (calcType === calcTypePercentage) {
        return size <= 100
      }
      if (calcType === calcTypeFixed) {
        return size.toString().length <= 19
      }
      return false
    }

    const primary_id_types: PrimaryIDType[] = this.state.primaryIDTypes || []
    const primary_id_type_ind = primary_id_types.findIndex(item => item.PrimaryIDType === editData.PrimaryIDType)
    const changes: PoolGeneralInfoEditValidity = {
      isPoolNameValid: editData.Name.trim().length >= 8,
      isFederallyRegulatedValid: editData.DOT !== undefined,
      isRegulatoryModeValid: !editData.DOT || editData.RegulatoryMode.length > 0,
      isSelectionIntervalValid: editData.SelectionInterval.length > 0,
      isCalcTypeValid: [calcTypePercentage, calcTypeFixed].includes(editData.FixedOrRate),
      isDrugSampleSizeValid: sampleSizeValid(editData.DrugSampleSize, editData.FixedOrRate),
      isAlcoholSampleSizeValid: sampleSizeValid(editData.AlcoholSampleSize, editData.FixedOrRate),
      isLabAccountValid: (isConsortium || editData.LabAccountNumber.length > 0),
      isLabPanelValid: (isConsortium || editData.Panel.length > 0),
      isPoolMembershipTypeValid: editData.UseFileImportForPoolMembership !== undefined,
      isPrimaryIDTypeValid: primary_id_type_ind >= 0,
      isPanelCodeValid: (!isConsortium || editData.PanelCode.length > 1)
    }
    const isValid = Object.values(changes).every((valid: boolean) => valid)
    if (!isValid) {
      this.setState(changes)
      return
    }

    const startDate = editData.ProgramIntervalStartDate ? parseISO(editData.ProgramIntervalStartDate) : undefined
    const endDate = editData.ProgramIntervalEndDate ? parseISO(editData.ProgramIntervalEndDate) : undefined
    const startDatePlusYear = editData.ProgramIntervalStartDate ? parseISO(editData.ProgramIntervalStartDate) : undefined
    startDatePlusYear && startDatePlusYear.setFullYear(startDatePlusYear.getFullYear() + 1)
    if (!startDate || !endDate || startDate >= endDate || !startDatePlusYear || startDatePlusYear < endDate) {
      return
    }

    switch (this.props.variant) {
      case PoolVariant.CloseProgramYear:
        this.performCloseProgramYearSave()
        break;
      default:
        this.setState({ showSaveConfirmModal: true })
        break;
    }
  }

  performSave = () => {
    this.setState({ saving: true, showSaveConfirmModal: false })
    const fixedOrRate = calcTypes.find(item => item.Code === this.state.editData?.FixedOrRate)?.ShortCode || ""
    this.updateSubscription && this.updateSubscription.unsubscribe()
    this.updateSubscription = PoolUpdateObservable({
      ...this.state.editData!,
      ProviderMappingID: this.state.selectedLabAccount?.ProviderMappingID,
      FixedOrRate: fixedOrRate
    }, !!this.props.isConsortium).subscribe(
      () => {
        toast.success("Updated pool details successfully.")
        this.setState({ saving: false })
        this.handleToggleEditMode()
        this.loadPoolData()
      },
      () => {
        this.setState({ saving: false })
        toast.error("Error updating pool details")
      }
    )
  }

  performCloseProgramYearSave = () => {
    if (!this.state.editData) {
      return
    }
    this.setState({ saving: true })
    this.closeProgramIntervalSubscription && this.closeProgramIntervalSubscription.unsubscribe()
    this.closeProgramIntervalSubscription = CloseProgramIntervalObservable({
      PoolID: this.state.editData?.PoolID || this.props.poolId,
      AlcoholTarget: this.state.editData?.AlcoholSampleSize,
      DrugTarget: this.state.editData?.DrugSampleSize,
      PanelID: this.state.editData?.PanelID,
      ProviderMappingID: this.state.selectedLabAccount?.ProviderMappingID || 0,
      DateFrom: format(parseISO(this.state.editData?.ProgramIntervalStartDate || ""), "MM/dd/yyyy"),
      DateTo: format(parseISO(this.state.editData?.ProgramIntervalEndDate || ""), "MM/dd/yyyy")
    }).subscribe(
      data => {
        toast.success(data)
        this.setState({ saving: false })
        this.handleToggleEditMode()
        this.loadPoolData()
      },
      error => {
        this.setState({ saving: false })
        toast.error(error.errorData?.message || error.message)
      }
    )
  }

  valueText = (text: string | number | undefined) => <Col xs={6} md={8} className="col-form-label">{text}</Col>

  rowClassName = (editable: boolean) => editable ? "form-group required" : this.state.editing ? "form-group" : ""

  openMissingPeriodsModal = () => this.setState({ showMissingModal: true })

  onSaveMissingPeriods = () => {
    this.setState({ showMissingModal: false })
    this.loadPoolData()
  }

  render() {
    const { data, editData, poolLoading, regulatoryModesLoading } = this.state
    const availableToSelectRegModes = this.state.availableToSelectRegModes || []
    const selectedRegulatoryModes = this.state.selectedRegulatoryModes || []
    const isPortalAdmin: boolean = this.props.isPortalAdmin || false
    if (poolLoading) {
      return <div className="d-flex justify-content-center">
        <Spinner animation="border" variant="secondary" style={{ margin: "auto" }} />
      </div>
    }
    const dataCalcType = calcTypes.find(item => item.Code === data?.FixedOrRate)
    const dataCalcTypeMark = data?.FixedOrRate === calcTypePercentage ? "%" : ""
    const isOnTrack = IsOnTrack()

    const editing = this.state.editing
    const editingAndNoRun = this.state.editing && !editData?.IsRunRandom
    const editingAndNoRunNonClosePY = editingAndNoRun && this.props.variant !== PoolVariant.CloseProgramYear
    const editable = {
      company: false,
      poolID: false,
      name: editingAndNoRunNonClosePY,
      programInterval: editingAndNoRun,
      regulated: editingAndNoRunNonClosePY,
      regulatoryMode: editingAndNoRunNonClosePY,
      selectionInterval: editingAndNoRunNonClosePY,
      calcType: editingAndNoRunNonClosePY,
      drug: editing,
      alcohol: editing,
      labAccount: editing,
      panel: editing,
      membershipType: editingAndNoRunNonClosePY,
      primaryIDType: editing,
      includeCompanies: editing,
      panelCode: editing,
      runOnlyByPortalAdmin: editing,
    }

    const startDateStr = (editing ? editData : data)?.ProgramIntervalStartDate
    const endDateStr = (editing ? editData : data)?.ProgramIntervalEndDate
    const startDate = startDateStr ? parseISO(startDateStr) : undefined
    const endDate = endDateStr ? parseISO(endDateStr) : undefined
    const startDatePlusYear = startDateStr ? parseISO(startDateStr) : undefined
    startDatePlusYear && startDatePlusYear.setFullYear(startDatePlusYear.getFullYear() + 1)
    const primaryIDType = (editable.primaryIDType) ? editData?.PrimaryIDType : data?.PrimaryIDType
    let regModes = data?.RegulatoryMode
    if (selectedRegulatoryModes.length > 0) {
      const names = this.state.selectedRegulatoryModes.map(({ Name }) => Name).join(", ")
      regModes = names
    }

    return <>
      <Form className="p-4 pt-3">
        <Form.Row>
          <Col>{(this.props.variant !== PoolVariant.CloseProgramYear) && <h5>MODIFY POOL CONFIGURATION</h5>}</Col>
          <Col>
            {!this.state.editing && data?.IsMissingPeriodTests && <Button variant="underline-link" className="m-2" onClick={() => this.openMissingPeriodsModal()}>
              Total tests performed for missed periods
            </Button>}
          </Col>
          <Col xs={1}>
            <h5>
              <Button variant="icon-link" size="sm" className="float-right ml-1 btn-icon-primary" onClick={this.handleToggleEditMode} hidden={!data || regulatoryModesLoading}>
                {!this.state.editing && <FaPencilAlt />}
                {this.state.editing && <FaTimes />}
              </Button>
            </h5>
          </Col>
        </Form.Row>
        {!this.props.isConsortium && <Form.Row className={this.rowClassName(editable.company)}>
          <Form.Label column xs={6} md={4}>COMPANY</Form.Label>
          {this.valueText(data?.Company)}
        </Form.Row>}
        <Form.Row className={this.rowClassName(editable.name)}>
          <Form.Label column xs={6} md={4}>POOL NAME</Form.Label>
          {!editable.name && this.valueText(data?.Name)}
          {editable.name &&
            <Col xs={6} md={8}>
              <Form.Control type="text" value={editData?.Name} onChange={this.handlePoolNameChange} isInvalid={!this.state.isPoolNameValid} />
              <Form.Control.Feedback type="invalid">Pool Name should contain at least 8 characters</Form.Control.Feedback>
            </Col>}
        </Form.Row>
        <Form.Row className={this.rowClassName(editable.poolID)}>
          <Form.Label column xs={6} md={4}>POOL ID</Form.Label>
          <Form.Label column xs={6} md={8}>{data?.Code}</Form.Label>
        </Form.Row>
        <Form.Row className={this.rowClassName(editable.programInterval)}>
          <Form.Label column xs={6} md={4}>PROGRAM INTERVAL</Form.Label>
          {!editable.programInterval && <Col xs={6} lg={1} className="col-form-label">{startDate ? format(startDate, "MM/dd/yyyy") : ""}</Col>}
          {editable.programInterval && <Col xs={6} lg={2} className="mb-1 mb-lg-0">
            <DatePicker className="form-control"
              peekNextMonth
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              required
              popperPlacement="top-end"
              selected={startDate}
              enableTabLoop={false}
              onChange={date => this.handleDateChange("ProgramIntervalStartDate", date as Date)} />
          </Col>}
          <Form.Label column xs={6} md={4} lg={2} xl={1} className="text-right pr-4">To</Form.Label>
          {!editable.programInterval && <Col xs={6} lg={1} className="col-form-label">{endDate ? format(endDate, "MM/dd/yyyy") : ""}</Col>}
          {editable.programInterval && <Col xs={6} lg={5}>
            <DatePicker className="form-control"
              peekNextMonth
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              required
              popperPlacement="top-end"
              selected={endDate}
              enableTabLoop={false}
              onChange={date => this.handleDateChange("ProgramIntervalEndDate", date as Date)} />
            {endDate && startDate && startDate >= endDate && <Form.Control.Feedback type="invalid" className="d-block">Date To should be later than Date From</Form.Control.Feedback>}
            {endDate && startDatePlusYear && startDatePlusYear < endDate && <Form.Control.Feedback type="invalid" className="d-block">The From and To dates cannot be more than one calendar year in duration</Form.Control.Feedback>}
          </Col>}
        </Form.Row>
        <Form.Row className={this.rowClassName(editable.regulated)}>
          <Form.Label column xs={6} md={4}>FEDERALLY REGULATED</Form.Label>
          {(!editable.regulated || !!this.props.isConsortium) && this.valueText(data?.DOT ? yes : no)}
          {editable.regulated && !this.props.isConsortium &&
            <Col xs={6} md={8}>
              <Typeahead id="federallyRegulated"
                ref={this.federallyRegulatedTypeahead}
                filterBy={() => true}
                onChange={this.handleFederallyRegulatedChange}
                options={[yes, no]}
                selected={editData?.DOT === undefined ? [] : [editData?.DOT ? yes : no]}
                isInvalid={!this.state.isFederallyRegulatedValid}
                emptyLabel="Select"
                placeholder="Select">
                {this.state.isFederallyRegulatedValid && <Button variant="toggle" onClick={() => { this.federallyRegulatedTypeahead.current?.toggleMenu() }}>
                  <FaAngleDown />
                </Button>}
              </Typeahead>
            </Col>}
        </Form.Row>
        {((!editing && data?.DOT) || (editing && editData?.DOT)) &&
          <Form.Row className={this.rowClassName(editable.regulatoryMode)}>
            <Form.Label column xs={6} md={4}>REGULATORY MODE(S)</Form.Label>
            <LoadingIndicatorWrapper loading={this.state.regulatoryModesLoading}>
              {!editable.regulatoryMode && this.valueText(regModes)}
            </LoadingIndicatorWrapper>
            {editable.regulatoryMode &&
              <Col xs={6} md={8}>
                <LoadingIndicatorWrapper loading={this.state.regulatoryModesLoading}>
                  <Typeahead id="regMode"
                    ref={this.regulatoryModeTypeahead}
                    labelKey="Name"
                    filterBy={(option, props) => props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                    onChange={(selected) => { this.handleRegulatoryModeChange(selected) }}
                    disabled={this.state.regulatoryModesLoading}
                    options={availableToSelectRegModes}
                    selected={selectedRegulatoryModes}
                    multiple
                    isInvalid={!this.state.isRegulatoryModeValid}
                    emptyLabel="No Regulatory Modes Found"
                    placeholder="Select">
                    {this.state.isRegulatoryModeValid && !this.state.regulatoryModesLoading && <Button variant="toggle" onClick={() => { this.regulatoryModeTypeahead.current?.toggleMenu() }}>
                      <FaAngleDown />
                    </Button>}
                  </Typeahead>
                </LoadingIndicatorWrapper>
              </Col>}
          </Form.Row>}
        <Form.Row className={this.rowClassName(editable.selectionInterval)}>
          <Form.Label column xs={6} md={4}>SELECTION INTERVAL</Form.Label>
          {!editable.selectionInterval && this.valueText(data?.SelectionInterval)}
          {editable.selectionInterval &&
            <Col xs={6} md={8}>
              <Typeahead id="selectionIntervals"
                ref={this.selectionIntervalTypeahead}
                filterBy={() => true}
                onChange={this.handleSelectionIntervalChange}
                disabled={this.state.selectionIntervalsLoading}
                options={this.state.selectionIntervals || []}
                selected={editData?.SelectionInterval ? [editData?.SelectionInterval] : []}
                isInvalid={!this.state.isSelectionIntervalValid}
                emptyLabel="No Selection Intervals Found"
                placeholder="Select">
                {this.state.isSelectionIntervalValid && !this.state.selectionIntervalsLoading && <Button variant="toggle" onClick={() => { this.selectionIntervalTypeahead.current?.toggleMenu() }}>
                  <FaAngleDown />
                </Button>}
              </Typeahead>
            </Col>}
        </Form.Row>
        <Form.Row className={this.rowClassName(editable.calcType)}>
          <Form.Label column xs={6} md={4}>POOL CALCULATION TYPE</Form.Label>
          {!editable.calcType && this.valueText(dataCalcType?.Name)}
          {editable.calcType &&
            <Col xs={6} md={8}>
              <Typeahead id="calcType"
                ref={this.calcTypeTypeahead}
                labelKey="Name"
                filterBy={() => true}
                onChange={this.handleCalcTypeChange}
                options={calcTypes}
                selected={this.state.selectedCalcType ? [this.state.selectedCalcType] : []}
                isInvalid={!this.state.isCalcTypeValid}
                emptyLabel="No Pool Calculation Types"
                placeholder="Select">
                {this.state.isCalcTypeValid && <Button variant="toggle" onClick={() => { this.calcTypeTypeahead.current?.toggleMenu() }}>
                  <FaAngleDown />
                </Button>}
              </Typeahead>
            </Col>}
        </Form.Row>
        <Form.Row className={this.rowClassName(editable.drug)}>
          <Form.Label column xs={6} md={4}>DRUG</Form.Label>
          {!editable.drug && this.valueText(`${data?.DrugSampleSize.toString()}${dataCalcTypeMark}`)}
          {editable.drug &&
            <Col xs={6} md={8}>
              <InputGroup>
                <Form.Control type="text" value={editData?.DrugSampleSize.toString()} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleSampleSizeChangeChange("DrugSampleSize", "isDrugSampleSizeValid", e.target.value)} isInvalid={!this.state.isDrugSampleSizeValid} />
                <InputGroup.Append hidden={this.state.editData?.FixedOrRate !== calcTypePercentage}><InputGroup.Text>%</InputGroup.Text></InputGroup.Append>
              </InputGroup>
            </Col>}
        </Form.Row>
        <Form.Row className={this.rowClassName(editable.alcohol)}>
          <Form.Label column xs={6} md={4}>ALCOHOL</Form.Label>
          {!editable.alcohol && this.valueText(`${data?.AlcoholSampleSize.toString()}${dataCalcTypeMark}`)}
          {editable.alcohol &&
            <Col xs={6} md={8}>
              <InputGroup>
                <Form.Control type="text" value={editData?.AlcoholSampleSize.toString()} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleSampleSizeChangeChange("AlcoholSampleSize", "isAlcoholSampleSizeValid", e.target.value)} isInvalid={!this.state.isAlcoholSampleSizeValid} />
                <InputGroup.Append hidden={this.state.editData?.FixedOrRate !== calcTypePercentage}><InputGroup.Text>%</InputGroup.Text></InputGroup.Append>
              </InputGroup>
            </Col>}
        </Form.Row>
        {this.props.isConsortium && <Form.Row className={this.rowClassName(editable.panelCode)}>
          <Form.Label column xs={6} md={4}>PANEL CODE</Form.Label>
          {!editable.panelCode && this.valueText(data?.PanelCode)}
          {editable.panelCode &&
            <Col xs={6} md={8}>
              <Form.Control type="text" disabled={(editData?.PanelID || 0) > 0} value={editData?.PanelCode} onChange={this.handlePanelCodeChange} isInvalid={!this.state.isPanelCodeValid} />
              <Form.Control.Feedback type="invalid">Panel Code should contain at least 2 characters</Form.Control.Feedback>
            </Col>}
        </Form.Row>}
        {!this.props.isConsortium && <Form.Row className={this.rowClassName(editable.labAccount)}>
          <Form.Label column xs={6} md={4}>DEFAULT LABORATORY ACCOUNT</Form.Label>
          {!editable.labAccount && this.valueText(data?.LabAccountNumber)}
          {editable.labAccount &&
            <Col xs={6} md={8}>
              <Typeahead id="labAccount"
                ref={this.labAccountTypeahead}
                labelKey="Description"
                filterBy={() => true}
                onChange={this.handleLabAccountChange}
                disabled={this.state.labAccountsLoading}
                options={this.state.labAccounts || []}
                selected={this.state.selectedLabAccount ? [this.state.selectedLabAccount] : []}
                isInvalid={!this.state.isLabAccountValid}
                emptyLabel="No Laboratory Accounts Found"
                placeholder="Select">
                {this.state.isLabAccountValid && !this.state.labAccountsLoading && <Button variant="toggle" onClick={() => { this.labAccountTypeahead.current?.toggleMenu() }}>
                  <FaAngleDown />
                </Button>}
              </Typeahead>
            </Col>}
        </Form.Row>}
        {!this.props.isConsortium && <Form.Row className={this.rowClassName(editable.panel)}>
          <Form.Label column xs={6} md={4}>PANEL</Form.Label>
          {!editable.panel && this.valueText(data?.Panel)}
          {editable.panel &&
            <Col xs={6} md={8}>
              <Typeahead id="labPanel"
                ref={this.panelTypeahead}
                labelKey="Description"
                filterBy={() => true}
                onChange={this.handleLabPanelChange}
                disabled={this.state.labPanelsLoading || !this.state.selectedLabAccount}
                options={this.state.availableLabPanels || []}
                selected={this.state.selectedLabPanel ? [this.state.selectedLabPanel] : []}
                isInvalid={!this.state.isLabPanelValid}
                emptyLabel="No Panels Found"
                placeholder="Select">
                {this.state.isLabPanelValid && !this.state.labPanelsLoading && <Button variant="toggle" onClick={() => { this.panelTypeahead.current?.toggleMenu() }}>
                  <FaAngleDown />
                </Button>}
              </Typeahead>
            </Col>}
        </Form.Row>}
        <Form.Row className={this.rowClassName(editable.membershipType)}>
          <Form.Label column xs={6} md={4}>POOL MEMBERSHIP TYPE</Form.Label>
          {!editable.membershipType && data?.UseFileImportForPoolMembership === true && this.valueText(poolMembershipTypeFileImport)}
          {!editable.membershipType && data?.UseFileImportForPoolMembership === false && this.valueText(poolMembershipTypeCriteria)}
          {editable.membershipType &&
            <Col xs={6} md={8}>
              <Typeahead id="poolMembershipType"
                ref={this.poolMembershipTypeTypeahead}
                filterBy={() => true}
                onChange={this.handlePoolMembershipTypeChange}
                options={[poolMembershipTypeFileImport, poolMembershipTypeCriteria]}
                selected={editData?.UseFileImportForPoolMembership === undefined ? [] : [editData?.UseFileImportForPoolMembership ? poolMembershipTypeFileImport : poolMembershipTypeCriteria]}
                isInvalid={!this.state.isPoolMembershipTypeValid}
                emptyLabel="Select"
                placeholder="Select">
                {this.state.isPoolMembershipTypeValid && <Button variant="toggle" onClick={() => { this.poolMembershipTypeTypeahead.current?.toggleMenu() }}>
                  <FaAngleDown />
                </Button>}
              </Typeahead>
            </Col>}
        </Form.Row>
        {!!this.props.isConsortium &&
          <Form.Row>
            <Form.Label column xs={6} md={4} htmlFor="includeNoSelection">INCLUDE COMPANIES WITH NO SELECTIONS</Form.Label>
            {!editable.includeCompanies && this.valueText(data?.IncludeCompaniesWithNoSelections ? yes : no)}
            {editable.includeCompanies && <Col xs={6} md={8}><Checkbox id="includeNoSelection" name="IncludeNoSelection" label="" checked={editData?.IncludeCompaniesWithNoSelections} className="pt-2" onChange={this.handleNoSelectionChange} /></Col>}
          </Form.Row>
        }
        {!isOnTrack && <Form.Row className="form-group required">
          <Form.Label column xs={6} md={4} htmlFor="primaryIDType">POOL REPORT ID TYPE</Form.Label>
          {!editable.primaryIDType && this.valueText(primaryIDType)}
          {editable.primaryIDType &&
            <Col xs={6} md={8}>
              <Form.Control id="primaryIDType"
                as="select"
                value={primaryIDType}
                disabled={this.state.primaryIDTypesLoading}
                isInvalid={!this.state.isPrimaryIDTypeValid}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => this.handlePrimaryIDTypeChange(e)}>
                {!primaryIDType && <option value=""></option>}
                {this.state.primaryIDTypes?.map(item => <option value={item.PrimaryIDType} key={item.PrimaryIDType}>{item.PrimaryIDType}</option>)}
              </Form.Control>
            </Col>}
        </Form.Row>}

        {!isOnTrack && <Form.Row>
          <Form.Label column xs={6} md={4}>WHO CAN RUN POOL</Form.Label>
          {!editable.runOnlyByPortalAdmin && this.valueText(data?.RunOnlyByPortalAdmin ? "Portal Administrator Only" : "Company Administrator Only")}
          {editable.runOnlyByPortalAdmin && <Col>
            <Checkbox id="runOnlyByCompanyAdmin" name="RunOnlyByCompanyAdmin" label="Company Administrator Only" checked={!editData?.RunOnlyByPortalAdmin} disabled={!isPortalAdmin} className="pt-2 ml-3" onChange={this.handleRunOnlyByCompanyAdminChange} />
          </Col>}
          {editable.runOnlyByPortalAdmin && <Col>
            <Checkbox id="runOnlyByPortalAdmin" name="RunOnlyByPortalAdmin" label="Portal Administrator Only" checked={editData?.RunOnlyByPortalAdmin} disabled={!isPortalAdmin} className="pt-2 ml-3" onChange={this.handleRunOnlyByPortalAdminChange} />
          </Col>}
        </Form.Row>}

        <Form.Row className="buttons-block" hidden={!this.state.editing}>
          <Button variant="secondary" onClick={this.handleToggleEditMode}>CANCEL</Button>
          <ActionButton variant="primary" onClick={this.handleSubmit} spin={this.state.saving} disableOnSpin>SAVE</ActionButton>
        </Form.Row>
      </Form>
      <ConfirmationModal show={this.state.showSaveConfirmModal}
        title="CONFIRMATION"
        textTitle="Are you sure you want to submit?"
        text="You have made modification to this pool configuration. Changes will be applied to the next random selection for this pool."
        onConfirm={this.performSave}
        onHide={() => this.setState({ showSaveConfirmModal: false })} />
      {this.state.showMissingModal && !!data && <MissedPeriodsModal details={data} onSave={() => this.onSaveMissingPeriods()} onHide={() => this.setState({ showMissingModal: false })} />}
    </>
  }
  /*
  <Form.Row className="form-group required">
                <Form.Label column sm={4} htmlFor="primaryIDType">Primary ID Type</Form.Label>
                <Col sm={8}>
                  <Form.Control id="primaryIDType"
                    as="select"
                    disabled={primaryIDTypesLoading || disabled}
                    value={primaryIDType}
                    isInvalid={!validPrimaryIDType}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => this.setState({ primaryIDType: Number(e.target.value) })}>
                    <option value="0">Select</option>
                    {primaryIDTypes?.map(item => <option value={item.PrimaryTypeID} key={item.PrimaryTypeID}>{item.PrimaryIDType}</option>)}
                  </Form.Control>
                </Col>
              </Form.Row>
  */
}
